import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { PlayCircle, Clock, Layers, X, Maximize, Check, CheckCircle2, Download } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { useAuth } from "../context/AuthContext";
import { fileUrl } from "../uploads";
import api from "../api";
import PdfViewer from "../components/PdfViewer";

function SectionPlayer({ section, cover }) {
  const src = fileUrl(section.url);
  const noDownload = !section.downloadable;

  if (section.type === "video") {
    return (
      <video
        key={section.id}
        controls
        autoPlay
        poster={cover || undefined}
        controlsList={noDownload ? "nodownload" : undefined}
        disablePictureInPicture={noDownload}
        onContextMenu={(e) => noDownload && e.preventDefault()}
        className="w-full h-full rounded-xl bg-black"
        src={src}
      />
    );
  }
  if (section.type === "audio") {
    return (
      <div className="w-full h-full flex items-center justify-center bg-plum/5 rounded-xl overflow-hidden relative">
        {cover && <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />}
        <audio key={section.id} controls autoPlay controlsList={noDownload ? "nodownload" : undefined} onContextMenu={(e) => noDownload && e.preventDefault()} className="w-full max-w-xl px-6 relative z-10" src={src} />
      </div>
    );
  }
  if (section.type === "image") {
    return <img key={section.id} src={src} alt={section.title} onContextMenu={(e) => noDownload && e.preventDefault()} className="w-full h-full rounded-xl object-contain bg-cream" />;
  }
  if (section.type === "pdf") {
    return <PdfViewer url={src} />;
  }
  return (
    <div className="text-center py-10 font-body text-ink/60 w-full h-full flex items-center justify-center">
      Este contenido no se puede previsualizar.
    </div>
  );
}

export default function Cursos() {
  const { courses } = useStore();
  const { user, isAdmin } = useAuth();
  const [active, setActive] = useState(null);
  const [activeSection, setActiveSection] = useState(null);
  const [viewed, setViewed] = useState({});
  const playerRef = useRef(null);

  const visibleCourses = isAdmin || !user?.stage ? courses : courses.filter((c) => c.stage === user.stage);

  useEffect(() => {
    if (!user) {
      setViewed({});
      return;
    }
    api.get("/progress").then(({ data }) => setViewed(data.viewed || {})).catch(() => {});
  }, [user]);

  const markViewed = (courseId, sectionId) => {
    if (!user || !courseId || !sectionId) return;
    if ((viewed[courseId] || []).includes(sectionId)) return;
    setViewed((prev) => ({ ...prev, [courseId]: [...(prev[courseId] || []), sectionId] }));
    api.post(`/progress/${courseId}/${sectionId}`).then(({ data }) => setViewed(data.viewed || {})).catch(() => {});
  };

  const courseProgress = (c) => {
    const total = (c.sections || []).length;
    if (!total) return null;
    const done = (viewed[c.id] || []).filter((sid) => (c.sections || []).some((s) => s.id === sid)).length;
    return { done, total, pct: Math.round((done / total) * 100) };
  };

  const selectSection = (courseId, section) => {
    setActiveSection(section);
    if (section) markViewed(courseId, section.id);
  };

  const openCourse = (c) => {
    setActive(c);
    const secs = c.sections || [];
    const first = secs.length ? secs[0] : null;
    setActiveSection(first);
    if (first) markViewed(c.id, first.id);
  };

  const goFullscreen = () => {
    const el = playerRef.current;
    if (!el) return;
    const req = el.requestFullscreen || el.webkitRequestFullscreen || el.msRequestFullscreen;
    if (req) {
      try {
        const p = req.call(el);
        if (p && p.catch) p.catch(() => {});
      } catch {}
    }
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-gradient-to-b from-amber/30 to-cream">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 text-center">
          <span className="font-sans text-xs uppercase tracking-[0.2em] text-terracotta">Contenido exclusivo</span>
          <h1 className="font-serif text-5xl md:text-6xl text-plum mt-3">Cursos online</h1>
          <p className="font-body text-lg text-ink/65 mt-4 max-w-2xl mx-auto">
            Formación paso a paso para acompañarte en cada etapa, a tu ritmo y desde donde quieras.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        {visibleCourses.length === 0 && (
          <p className="text-center font-body text-ink/50 py-16">
            Aún no hay cursos disponibles en tu espacio. ¡Pronto añadiremos contenido nuevo!
          </p>
        )}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
          {visibleCourses.map((c) => {
            const count = c.sections?.length || c.lessons || 0;
            const prog = courseProgress(c);
            return (
              <div key={c.id} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
                <div className="relative h-52 overflow-hidden">
                  <img src={fileUrl(c.cover)} alt={c.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                  <button onClick={() => openCourse(c)} className="absolute inset-0 bg-ink/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <PlayCircle size={54} className="text-cream" />
                  </button>
                  <span className="absolute top-4 left-4 font-sans text-[10px] uppercase tracking-[0.15em] text-plum bg-cream/90 px-3 py-1 rounded-full">{c.stage}</span>
                  {prog && prog.pct === 100 && (
                    <span data-testid={`course-completed-${c.id}`} className="absolute top-4 right-4 flex items-center gap-1 font-sans text-[10px] uppercase tracking-[0.12em] text-white bg-terracotta px-2.5 py-1 rounded-full">
                      <CheckCircle2 size={12} /> Completado
                    </span>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="font-serif text-2xl text-plum leading-tight">{c.title}</h3>
                  <p className="font-body text-ink/65 text-sm mt-2 leading-relaxed line-clamp-2">{c.description}</p>
                  <div className="flex items-center gap-4 mt-4 font-body text-xs text-ink/50">
                    <span className="flex items-center gap-1.5"><Layers size={14} /> {count} secciones</span>
                    {c.duration && c.duration !== "—" && <span className="flex items-center gap-1.5"><Clock size={14} /> {c.duration}</span>}
                  </div>
                  {prog && (
                    <div className="mt-4" data-testid={`course-progress-${c.id}`}>
                      <div className="flex items-center justify-between font-body text-xs text-ink/55 mb-1.5">
                        <span>Tu progreso</span>
                        <span className="font-semibold text-plum">{prog.done}/{prog.total} · {prog.pct}%</span>
                      </div>
                      <div className="h-2 rounded-full bg-amber/30 overflow-hidden">
                        <div className="h-full bg-terracotta rounded-full transition-all duration-500" style={{ width: `${prog.pct}%` }} />
                      </div>
                    </div>
                  )}
                  <button onClick={() => openCourse(c)} className="w-full mt-5 bg-plum text-cream font-sans py-3 rounded-full hover:bg-[#684d89] transition-colors">
                    {prog && prog.done > 0 ? "Continuar curso" : "Ver curso"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {active && createPortal(
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4 bg-ink/70 backdrop-blur-sm animate-fade-in" onClick={() => setActive(null)}>
          <div className="bg-cream rounded-[1.25rem] w-full max-w-[1700px] h-[95vh] sm:h-[97vh] overflow-y-auto overflow-x-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative">
              {/* Header */}
              <div className="flex items-start justify-between p-4 sm:p-6 pb-3 sm:pb-4 gap-3">
                <div className="min-w-0">
                  <span className="font-sans text-[11px] uppercase tracking-[0.15em] text-terracotta">{active.stage}</span>
                  <h3 className="font-serif text-2xl sm:text-3xl text-plum leading-tight">{active.title}</h3>
                </div>
                <button onClick={() => setActive(null)} data-testid="close-course-btn" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-plum hover:bg-amber/30 shrink-0">
                  <X size={18} />
                </button>
              </div>

              {(active.sections && active.sections.length > 0) ? (
                <div className="px-4 sm:px-6 pb-6 grid lg:grid-cols-[1fr_320px] gap-5 lg:gap-6">
                  {/* Player */}
                  <div>
                    {activeSection ? (
                      <>
                        <div ref={playerRef} className="relative w-full h-[38vh] sm:h-[52vh] lg:h-[78vh] bg-cream rounded-xl overflow-hidden group/player">
                          <SectionPlayer section={activeSection} cover={fileUrl(active.cover)} />
                          <button
                            onClick={goFullscreen}
                            data-testid="fullscreen-btn"
                            title="Pantalla completa"
                            className="absolute top-3 right-3 z-10 flex items-center gap-1.5 bg-ink/60 hover:bg-plum text-cream font-sans text-xs px-3 py-2 rounded-full backdrop-blur-sm transition-colors opacity-100 lg:opacity-0 lg:group-hover/player:opacity-100 focus:opacity-100"
                          >
                            <Maximize size={15} /> <span className="hidden sm:inline">Pantalla completa</span>
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <h4 className="font-serif text-xl text-plum">{activeSection.title}</h4>
                          {activeSection.downloadable && (
                            <a href={fileUrl(activeSection.url, true)} className="flex items-center gap-1.5 font-sans text-sm text-terracotta hover:text-plum transition-colors">
                              <Download size={16} /> Descargar
                            </a>
                          )}
                        </div>
                      </>
                    ) : (
                      <p className="font-body text-ink/60 py-10 text-center">Selecciona una sección para empezar.</p>
                    )}
                    <p className="font-body text-ink/65 text-sm mt-4 leading-relaxed">{active.description}</p>
                  </div>

                  {/* Section list */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-sans uppercase tracking-[0.12em] text-xs text-terracotta">Contenido del curso</p>
                      {(() => { const p = courseProgress(active); return p ? <span className="font-body text-xs font-semibold text-plum">{p.pct}%</span> : null; })()}
                    </div>
                    {(() => {
                      const p = courseProgress(active);
                      return p ? (
                        <div className="h-2 rounded-full bg-amber/30 overflow-hidden mb-4">
                          <div className="h-full bg-terracotta rounded-full transition-all duration-500" style={{ width: `${p.pct}%` }} />
                        </div>
                      ) : null;
                    })()}
                    <div className="space-y-2 lg:max-h-[78vh] lg:overflow-y-auto lg:pr-1">
                      {active.sections.map((s, i) => {
                        const isActive = activeSection && activeSection.id === s.id;
                        const isDone = (viewed[active.id] || []).includes(s.id);
                        return (
                          <button
                            key={s.id || i}
                            data-testid={`section-item-${i}`}
                            onClick={() => selectSection(active.id, s)}
                            className={`w-full flex items-center gap-3 text-left rounded-xl px-3 py-2.5 transition-colors ${
                              isActive ? "bg-plum text-cream" : "bg-white text-ink/75 hover:bg-amber/20"
                            }`}
                          >
                            <span
                              className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-serif text-base font-semibold ${
                                isActive ? "bg-cream/20 text-cream" : isDone ? "bg-terracotta text-cream" : "bg-amber/40 text-plum"
                              }`}
                            >
                              {isDone && !isActive ? <Check size={16} /> : i + 1}
                            </span>
                            <span className="font-body text-sm leading-tight flex-1">{s.title}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ) : (
                /* Legacy course without sections (modules text) */
                <div className="px-6 pb-7">
                  <img src={fileUrl(active.cover)} alt={active.title} className="w-full h-44 object-cover rounded-xl" />
                  <p className="font-body text-ink/65 mt-4">{active.description}</p>
                  {active.modules && active.modules.length > 0 && (
                    <>
                      <h4 className="font-sans uppercase tracking-[0.15em] text-xs text-terracotta mt-6 mb-3">Contenido del curso</h4>
                      <ul className="space-y-2.5">
                        {active.modules.map((m, i) => (
                          <li key={i} className="flex items-center gap-3 font-body text-ink/75 bg-white rounded-xl px-4 py-3">
                            <span className="w-6 h-6 rounded-full bg-amber/40 flex items-center justify-center text-plum text-xs font-semibold">{i + 1}</span>
                            {m}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
